/**
 * Cart drawer.
 *
 * All cart state comes from Shopify. After every mutation the drawer asks the
 * Section Rendering API for fresh markup and swaps it in, so prices, discounts
 * and totals are never recalculated in the browser.
 *
 * Events are delegated from the drawer root, so re-rendered markup keeps
 * working without rebinding anything.
 */
(function () {
  'use strict';

  const { a11y, cart, pubsub, utils } = window.Theme;

  /**
   * Everything a cart line can do, shared by the drawer and the cart page.
   *
   * Both render the same snippet (cart-line-items.liquid), so both need the
   * same stepper, remove, error and focus-restoration behaviour. Putting it
   * here rather than duplicating it is what keeps /cart and the drawer from
   * drifting apart as the theme changes.
   */
  class CartSurface extends HTMLElement {
    /** Wires the line controls. Call from connectedCallback with a signal. */
    bindLineEvents() {
      this.addEventListener('click', (event) => {
        const stepper = event.target.closest('[data-quantity-change]');
        if (stepper) {
          event.preventDefault();
          this.stepQuantity(stepper);
          return;
        }

        const remove = event.target.closest('[data-cart-remove]');
        if (remove) {
          // A real link to Shopify's remove route, so it works without this
          // script. With the script, it becomes a Cart API call instead.
          event.preventDefault();
          const line = remove.closest('[data-cart-item]');
          this.updateQuantity(line, 0);
        }
      });

      this.addEventListener(
        'change',
        utils.debounce((event) => {
          const input = event.target.closest('[data-quantity-input]');
          if (!input) return;
          const line = input.closest('[data-cart-item]');
          this.updateQuantity(line, parseInt(input.value, 10));
        }, 300)
      );

      // The note posts with the checkout form, but saving it as the visitor
      // types means it survives them navigating away and coming back.
      this.addEventListener(
        'input',
        utils.debounce((event) => {
          const note = event.target.closest('[data-cart-note]');
          if (!note) return;
          fetch(`${window.Theme.config.routes.cartUpdate}`, utils.fetchConfig('json', { note: note.value }));
        }, 600)
      );
    }

    stepQuantity(button) {
      const line = button.closest('[data-cart-item]');
      const input = line.querySelector('[data-quantity-input]');
      const delta = parseInt(button.dataset.quantityChange, 10);
      const next = Math.max(0, parseInt(input.value, 10) + delta);
      this.updateQuantity(line, next);
    }

    async updateQuantity(line, quantity) {
      if (!line || Number.isNaN(quantity)) return;

      const lineIndex = parseInt(line.dataset.line, 10);
      this.setLoading(line, true);
      this.clearLineError(line);

      try {
        await cart.change({ line: lineIndex, quantity });
      } catch (error) {
        this.showLineError(line, error.data?.description || window.Theme.strings.cartError);
        this.setLoading(line, false);
      }
    }

    setLoading(line, isLoading) {
      line.dataset.loading = isLoading ? 'true' : 'false';
      line.querySelectorAll('button, input').forEach((el) => {
        el.disabled = isLoading;
      });
    }

    showLineError(line, message) {
      const target = line.querySelector('[data-cart-item-error]');
      if (!target) return;
      target.textContent = message;
      target.hidden = false;
    }

    clearLineError(line) {
      const target = line.querySelector('[data-cart-item-error]');
      if (!target) return;
      target.textContent = '';
      target.hidden = true;
    }

    /**
     * Describes a control well enough to find its replacement after a
     * re-render: which line it belonged to and what it does. Line keys survive
     * a quantity change, so "the plus button on line X" still resolves to the
     * right element in the new markup.
     */
    describeControl(el) {
      const line = el.closest('[data-cart-item]');
      if (!line) return null;

      return {
        // The line item key, which Shopify keeps stable across a quantity
        // change — unlike the line index, which shifts when a line is removed.
        key: line.dataset.key,
        step: el.dataset.quantityChange || null,
        isInput: el.matches('[data-quantity-input]') || null,
        isRemove: Boolean(el.closest('[data-cart-remove]')) || null,
      };
    }

    restoreControl(target, fallback) {
      if (!target || !target.key) return;
      const line = this.querySelector(`[data-cart-item][data-key="${CSS.escape(target.key)}"]`);
      if (!line) {
        // The line is gone — removed, or the quantity hit zero. Move focus
        // somewhere sensible rather than dropping it at the top of the page.
        fallback?.focus({ preventScroll: true });
        return;
      }

      let next = null;
      if (target.step) next = line.querySelector(`[data-quantity-change="${target.step}"]`);
      else if (target.isInput) next = line.querySelector('[data-quantity-input]');
      else if (target.isRemove) next = line.querySelector('[data-cart-remove]');

      next?.focus({ preventScroll: true });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Drawer                                                              */
  /* ------------------------------------------------------------------ */

  class CartDrawer extends CartSurface {
    connectedCallback() {
      // Every listener this element puts on `document` carries this signal,
      // so disconnectedCallback detaches all of them in one call.
      this.controller = new AbortController();
      this.signal = this.controller.signal;

      this.sectionId = this.dataset.sectionId;
      this.overlay = document.querySelector('[data-cart-overlay]');
      this.body = this.querySelector('[data-cart-body]');
      this.footer = this.querySelector('[data-cart-footer]');
      this.closeButton = this.querySelector('[data-cart-close]');

      // Tell the cart which sections to return with every mutation.
      cart.registerSection(this.sectionId);
      cart.registerSection('cart-icon-bubble');

      this.bindOpeners();
      this.bindLineEvents();

      this.unsubscribe = pubsub.subscribe(pubsub.EVENTS.cartUpdate, (data) => this.onCartUpdate(data));

      this.closeButton?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());
      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape' && this.isOpen) this.close();
        },
        { signal: this.signal }
      );
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.unsubscribe?.();
      if (this.isOpen) {
        // The drawer is being replaced while open — release the page rather
        // than leaving the body scroll-locked with no drawer to close.
        a11y.removeTrapFocus(false);
        a11y.unlockScroll();
      }
    }

    /* ---------------- Opening ---------------- */

    /**
     * The openers live in the header, not in this element.
     *
     * Binding each one individually broke in two directions: when the header
     * re-rendered, the new buttons had no handler and the drawer stopped
     * opening; when the drawer re-rendered, the surviving buttons collected a
     * second handler. One delegated listener on `document` is immune to both,
     * because it matches whatever is in the DOM at click time.
     */
    bindOpeners() {
      document.addEventListener(
        'click',
        (event) => {
          const trigger = event.target.closest('[data-cart-drawer-toggle]');
          if (!trigger) return;
          event.preventDefault();
          this.open();
        },
        { signal: this.signal }
      );
    }

    open() {
      this.hidden = false;
      this.overlay.hidden = false;

      // Force a layout pass so the transition has a starting point, then set
      // the state synchronously. requestAnimationFrame would be tidier but it
      // does not fire in a background or non-compositing tab, which would
      // leave the drawer open in the DOM yet invisible.
      void this.offsetWidth;
      this.dataset.open = 'true';
      this.overlay.dataset.open = 'true';

      this.isOpen = true;
      document.querySelectorAll('[data-cart-drawer-toggle]').forEach((t) => {
        t.setAttribute('aria-expanded', 'true');
      });

      a11y.lockScroll();
      a11y.trapFocus(this, this.closeButton || this);
    }

    close() {
      if (!this.isOpen) return;

      this.dataset.open = 'false';
      this.overlay.dataset.open = 'false';
      this.isOpen = false;

      document.querySelectorAll('[data-cart-drawer-toggle]').forEach((t) => {
        t.setAttribute('aria-expanded', 'false');
      });

      a11y.removeTrapFocus(true);
      a11y.unlockScroll();

      const onDone = () => {
        if (this.dataset.open !== 'true') {
          this.hidden = true;
          this.overlay.hidden = true;
        }
      };
      this.addEventListener('transitionend', onDone, { once: true });
      setTimeout(onDone, 500);
    }

    /* ---------------- Line item interaction ---------------- */

    /* ---------------- Re-render ---------------- */

    onCartUpdate({ sections = {} }) {
      const drawerHtml = sections[this.sectionId];
      if (drawerHtml) {
        // Replacing the body destroys whatever the visitor was using. Someone
        // pressing "+" three times would lose focus to <body> after the first
        // press and have to tab back every time, so the control is identified
        // before the swap and re-found afterwards.
        const active = document.activeElement;
        const restore = this.contains(active) ? this.describeControl(active) : null;
        const scrollTop = this.body.scrollTop;

        const fresh = utils.parseSection(drawerHtml, '[data-cart-body]');
        const freshFooter = utils.parseSection(drawerHtml, '[data-cart-footer]');
        if (fresh) this.body.innerHTML = fresh.innerHTML;
        if (freshFooter) this.footer.innerHTML = freshFooter.innerHTML;

        this.body.scrollTop = scrollTop;
        if (restore) this.restoreControl(restore, this.closeButton);
      }

      const bubbleHtml = sections['cart-icon-bubble'];
      if (bubbleHtml) {
        const fresh = utils.parseSection(bubbleHtml, '[data-cart-count]');
        document.querySelectorAll('[data-cart-count]').forEach((node) => {
          if (fresh) node.replaceWith(fresh.cloneNode(true));
        });
      }

      this.announce();
    }

    /**
     * Reads the count from the freshly rendered cart bubble rather than from
     * the response, because /cart/add.js does not include a cart total.
     */
    announce() {
      const region = document.getElementById('a11y-live-region');
      const bubble = document.querySelector('[data-cart-count]');
      if (!region || !bubble) return;
      const count = bubble.dataset.count;
      if (count === undefined) return;
      region.textContent = window.Theme.strings.cartCount
        ? window.Theme.strings.cartCount.replace('[count]', count)
        : count;
    }
  }

  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  /* ------------------------------------------------------------------ */
  /* Cart page                                                           */
  /* ------------------------------------------------------------------ */

  /**
   * <cart-page> — the same cart, at /cart.
   *
   * It shares every line behaviour with the drawer through CartSurface, and
   * re-renders itself from the Section Rendering API after each change, so
   * the totals it shows are always Shopify's. That is what lets the page drop
   * the old "Update" button: a quantity change applies immediately instead of
   * waiting for a form submit.
   *
   * The markup underneath is still a real Shopify cart form, so with this
   * script blocked the update and checkout buttons submit it as normal.
   */
  class CartPage extends CartSurface {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;

      // Ask for this section back with every cart mutation, so the subtotal,
      // the free shipping bar and the discounts all re-render together.
      cart.registerSection(this.sectionId);
      cart.registerSection('cart-icon-bubble');

      this.bindLineEvents();
      this.unsubscribe = pubsub.subscribe(pubsub.EVENTS.cartUpdate, (data) => this.onCartUpdate(data));
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    onCartUpdate({ sections = {} }) {
      const html = sections[this.sectionId];
      if (!html) return;

      const fresh = utils.parseSection(html, 'cart-page');
      if (!fresh) return;

      // Same reasoning as the drawer: keep the visitor's place and their
      // keyboard focus across the swap.
      const active = document.activeElement;
      const restore = this.contains(active) ? this.describeControl(active) : null;
      const scrollY = window.scrollY;

      this.innerHTML = fresh.innerHTML;

      window.scrollTo({ top: scrollY });
      if (restore) this.restoreControl(restore, this.querySelector('[name="checkout"]'));

      const bubble = sections['cart-icon-bubble'];
      if (bubble) {
        const count = utils.parseSection(bubble, '[data-cart-count]');
        document.querySelectorAll('[data-cart-count]').forEach((node) => {
          if (count) node.replaceWith(count.cloneNode(true));
        });
      }
    }
  }

  if (!customElements.get('cart-page')) customElements.define('cart-page', CartPage);

  /* ------------------------------------------------------------------ */
  /* Cart upsell and cart gift                                           */
  /*                                                                     */
  /* Both live inside the drawer body, which is replaced wholesale on     */
  /* every cart update, so their behaviour is delegated from the document */
  /* rather than bound to elements that will not survive.                 */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-upsell-add], [data-cart-gift-add]');
    if (!button || !button.closest('cart-drawer')) return;

    event.preventDefault();
    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    button.classList.add('button--loading');
    button.setAttribute('aria-disabled', 'true');

    try {
      await cart.add({ id: variantId, quantity: 1 });
      // The drawer re-renders from the response, so the button goes with it.
    } catch {
      button.classList.remove('button--loading');
      button.removeAttribute('aria-disabled');
    }
  });

  /**
   * Fills the cart upsell from Shopify's Recommendations API. The cards are
   * rendered by Liquid in sections/product-recommendations.liquid so they
   * match the rest of the theme instead of being assembled in JavaScript.
   */
  async function loadCartRecommendations(scope = document) {
    const host = scope.querySelector('cart-upsell[data-source="recommendations"][data-product-id]');
    if (!host || host.dataset.loaded === 'true') return;

    const url = new URL(`${window.Theme.config.routes.root}recommendations/products`, window.location.origin);
    url.searchParams.set('section_id', 'product-recommendations');
    url.searchParams.set('product_id', host.dataset.productId);
    url.searchParams.set('limit', host.dataset.limit || 2);

    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const fresh = utils.parseSection(await response.text(), '[data-upsell-list]');
      const target = host.querySelector('[data-upsell-list]');
      if (fresh && target && fresh.children.length) {
        target.replaceWith(fresh);
        host.hidden = false;
      }
      host.dataset.loaded = 'true';
    } catch {
      // A failed recommendation fetch must never break the cart.
    }
  }

  pubsub.subscribe(pubsub.EVENTS.cartUpdate, () => {
    loadCartRecommendations();
    autoAddGift();
  });

  /**
   * Adds the gift line once the threshold is reached, when the merchant asked
   * for that. Guarded so a re-render never triggers a second add.
   */
  let giftPending = false;
  async function autoAddGift() {
    const gift = document.querySelector('cart-gift');
    if (
      !gift ||
      giftPending ||
      gift.dataset.autoAdd !== 'true' ||
      gift.dataset.unlocked !== 'true' ||
      gift.dataset.inCart === 'true'
    ) {
      return;
    }

    giftPending = true;
    try {
      await cart.add({ id: Number(gift.dataset.variantId), quantity: 1 });
    } catch {
      // Sold out or otherwise unavailable — leave the cart untouched.
    } finally {
      giftPending = false;
    }
  }

  loadCartRecommendations();

  /**
   * Opens the drawer when something is ADDED to the cart. Quantity changes and
   * removals publish cart:updated only, so editing the cart never re-opens a
   * drawer the customer just closed.
   */
  pubsub.subscribe(pubsub.EVENTS.cartAdd, () => {
    if (window.Theme.config.atcBehaviour !== 'drawer') return;
    document.querySelector('cart-drawer')?.open();
  });
})();
